// src/components/GatheringDetailModal.tsx
import React, { useEffect, useState } from 'react';
import { Modal, Box, IconButton, CircularProgress, Typography, Backdrop, Button } from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import { Gathering, Area, GatheringCheckIn } from '../../../../types';
import { fetchGatheringById, updateGatheringDetails, updateGatheringSchedule } from '../../../../services/api/endpoints/gatherings';
import {fetchCheckInsByGatheringId} from '../../../../services/api';
import { fetchAreaById } from '../../../../services/api/endpoints/spaces';
import { gatheringDataService } from '../../../../services/data/gatheringService';
import GatheringDetailContent from './GatheringDetailContent';
import GatheringEditForm from './GatheringEditForm';
import RescheduleModal from '../modals/RescheduleModal';
import TransferOwnershipDialog from './TransferOwnershipDialog';
import { updateGathering } from '../../../../services/data/store';

interface GatheringDetailModalProps {
  gatheringId: string | null;
  isOpen: boolean;
  onClose: () => void;
  token: string;
}

// Style for the modal content using MUI v7 patterns
const modalStyle = {
  position: 'absolute',
  top: '50%',
  left: '50%',
  transform: 'translate(-50%, -50%)',
  width: '90%',
  maxWidth: '1200px',
  maxHeight: '90vh',
  bgcolor: 'background.paper',
  boxShadow: 24,
  p: { xs: 2, sm: 4 },
  borderRadius: 2,
  outline: 0,
  overflow: 'auto'
};

const GatheringDetailModal: React.FC<GatheringDetailModalProps> = ({
  gatheringId,
  isOpen,
  onClose,
  token
}) => {
  const [gathering, setGathering] = useState<Gathering | null>(null);
  const [loading, setLoading] = useState<boolean>(false);
  const [error, setError] = useState<string | null>(null);
  const [areasLoading, setAreasLoading] = useState<boolean>(false);
  
  // State for edit mode
  const [isEditMode, setIsEditMode] = useState<boolean>(false);
  // State for tracking save operation
  const [isSaving, setIsSaving] = useState<boolean>(false);
  // State for reschedule modal
  const [isRescheduleModalOpen, setIsRescheduleModalOpen] = useState<boolean>(false);
  // State for transfer ownership modal
  const [isTransferOwnershipModalOpen, setIsTransferOwnershipModalOpen] = useState<boolean>(false);
  const [gatheringCheckIns, setGatheringCheckIns] = useState<GatheringCheckIn[]>([]);
  const [checkInsLoading, setCheckInsLoading] = useState(false);

useEffect(() => {
  if (isOpen && gatheringId) {
    setCheckInsLoading(true);
    fetchCheckInsByGatheringId(token, gatheringId)
      .then(setGatheringCheckIns)
      .catch(err => console.error("Error fetching gathering check-ins:", err))
      .finally(() => setCheckInsLoading(false));
  }
}, [isOpen, gatheringId, token]);


  useEffect(() => {
    if (isOpen && gatheringId) {
      setLoading(true);
      setError(null);
      
      fetchGatheringById(token, gatheringId)
        .then(data => {
          // Remove mock area data - we'll use the actual areas from the API
          setGathering(data);
          setLoading(false);
          
          // Fetch complete area details if areas exist
          if (data.areas && data.areas.length > 0) {
            setAreasLoading(true);
            
            // Create an array of promises to fetch all area details
            const areaPromises = data.areas.map(area => 
              fetchAreaById(token, area.id)
                .catch(err => {
                  console.error(`Error fetching area ${area.id}:`, err);
                  // Return the original area if there's an error
                  return area;
                })
            );
            
            // Wait for all area details to be fetched
            Promise.all(areaPromises)
              .then(areasWithDetails => {
                // Update the gathering with the complete area details
                setGathering(prevGathering => {
                  if (!prevGathering) return null;
                  return {
                    ...prevGathering,
                    areas: areasWithDetails
                  };
                });
                setAreasLoading(false);
              })
              .catch(err => {
                console.error('Error fetching area details:', err);
                setAreasLoading(false);
              });
          }
        })
        .catch(err => {
          console.error('Error fetching gathering details:', err);
          setError('Failed to load gathering details. Please try again.');
          setLoading(false);
        });
    } else {
      // Reset state when modal closes
      setGathering(null);
      setIsEditMode(false);
      setIsRescheduleModalOpen(false);
    }
  }, [gatheringId, isOpen, token]);

  // Function to toggle edit mode
  const toggleEditMode = () => {
    setIsEditMode(!isEditMode);
  };

  // Handle saving the edited gathering
  const handleSaveGathering = async (updatedGathering: Partial<Gathering>) => {
    if (!gathering) return;
    
    setIsSaving(true);
    try {
      // Set the token for the service
      gatheringDataService.setToken(token);
      
      // Update the gathering
      const savedGathering = await gatheringDataService.updateGatheringDetails(
        gathering.id,
        updatedGathering
      );
      
      // Update the local state with the saved gathering
      setGathering(savedGathering);
      
      // Exit edit mode
      setIsEditMode(false);
    } catch (error) {
      console.error('Error saving gathering:', error);
      setError('Failed to save changes. Please try again.');
    } finally {
      setIsSaving(false);
    }
  };

  // Handle opening the reschedule modal
  const handleRescheduleClick = () => {
    setIsRescheduleModalOpen(true);
  };

  // Handle opening the transfer ownership modal
  const handleTransferOwnershipClick = () => {
    setIsTransferOwnershipModalOpen(true);
  };

  // Handle a pending edit being approved or rejected (ENG-1097).
  //
  // The approve/reject endpoints return the updated gathering, but that payload
  // is built for the acting admin and the board's store copy also needs the
  // fresh values. So: apply the returned gathering immediately (instant
  // feedback, panel disappears), push it into the store so the card re-renders,
  // then re-read the gathering to confirm the staged edit is really gone. The
  // endpoint already invalidated the cache, so the re-read hits the API.
  const handlePendingEditReviewed = async (updatedGathering: Gathering) => {
    setGathering(updatedGathering);
    updateGathering(updatedGathering);

    try {
      const refreshedGathering = await fetchGatheringById(token, updatedGathering.id);
      setGathering(refreshedGathering);
      updateGathering(refreshedGathering);
    } catch (err) {
      // Non-fatal: the decision landed and the returned gathering is already
      // rendered. Only the confirmation re-read failed.
      console.error('Error refreshing gathering after pending-edit review:', err);
    }
  };

  // Handle successful ownership transfer
  const handleTransferOwnershipSuccess = async () => {
    if (!gathering) return;
    
    // Refresh the gathering data
    try {
      const refreshedGathering = await fetchGatheringById(token, gathering.id);
      setGathering(refreshedGathering);
      
      // Update the store with the refreshed gathering
      updateGathering(refreshedGathering);
    } catch (error) {
      console.error('Error refreshing gathering after ownership transfer:', error);
    }
  };

  // Handle saving the rescheduled gathering
  const handleSaveReschedule = async (scheduleData: {
    date: string;
    start_time: string;
    end_time: string;
    duration: number;
    space_id: string;
    area_ids: string[];
  }) => {
    if (!gathering) return;
    
    setIsSaving(true);
    try {
      // Use the specialized updateGatheringSchedule function directly
      const savedGathering = await updateGatheringSchedule(
        token,
        gathering.id,
        scheduleData
      );
      
      // Update the local state with the saved gathering
      setGathering(savedGathering);
      
      // Update the store with the saved gathering
      // This ensures the columns are updated immediately, just like with title changes
      updateGathering(savedGathering);
    } catch (error) {
      console.error('Error saving reschedule:', error);
      throw new Error('Failed to save the new schedule. Please try again.');
    } finally {
      setIsSaving(false);
    }
  };

  const handleClose = () => {
    onClose();
  };

  return (
    <Modal
      open={isOpen}
      onClose={handleClose}
      aria-labelledby="gathering-detail-modal"
      aria-describedby="detailed view of a gathering"
      closeAfterTransition
      slots={{ backdrop: Backdrop }}
      slotProps={{
        backdrop: {
          timeout: 500,
        },
      }}
    >
      <Box sx={modalStyle}>
        {/* Close button in top-right corner */}
        <IconButton
          aria-label="close"
          onClick={handleClose}
          sx={{
            position: 'absolute',
            right: 16,
            top: 16,
            color: 'grey.500',
            bgcolor: 'rgba(255, 255, 255, 0.8)',
            '&:hover': {
              bgcolor: 'rgba(255, 255, 255, 0.9)',
              color: 'grey.800',
            },
            zIndex: 10,
          }}
        >
          <CloseIcon />
        </IconButton>
        
        {loading ? (
          <Box sx={{ 
            display: 'flex', 
            justifyContent: 'center', 
            alignItems: 'center',
            flexDirection: 'column',
            minHeight: '300px',
            my: 4 
          }}>
            <CircularProgress size={60} thickness={4} />
            <Typography variant="body1" sx={{ mt: 2, color: 'text.secondary' }}>
              Loading gathering details...
            </Typography>
          </Box>
        ) : error ? (
          <Box sx={{ 
            color: 'error.main', 
            textAlign: 'center', 
            my: 4,
            p: 3,
            border: '1px solid',
            borderColor: 'error.light',
            borderRadius: 1,
            bgcolor: 'error.lightest'
          }}>
            <Typography variant="h6" color="error" gutterBottom>
              Error
            </Typography>
            <Typography variant="body1">{error}</Typography>
          </Box>
        ) : gathering ? (
          <>
            {isEditMode ? (
              <GatheringEditForm 
                gathering={gathering}
                onCancel={toggleEditMode}
                onSave={handleSaveGathering}
                isSaving={isSaving}
                token={token}
              />
            ) : (
              <GatheringDetailContent
                gathering={gathering}
                checkIns={gatheringCheckIns}
                checkInsLoading={checkInsLoading}
                onEditClick={toggleEditMode}
                onRescheduleClick={handleRescheduleClick}
                onTransferOwnershipClick={handleTransferOwnershipClick}
                token={token}
                onPendingEditReviewed={handlePendingEditReviewed}
              />
            )}

            {/* Reschedule Modal */}
            {gathering && (
              <RescheduleModal
                open={isRescheduleModalOpen}
                onClose={() => setIsRescheduleModalOpen(false)}
                gathering={gathering}
                onSave={handleSaveReschedule}
                token={token}
              />
            )}

            {/* Transfer Ownership Modal */}
            {gathering && (
              <TransferOwnershipDialog
                open={isTransferOwnershipModalOpen}
                onClose={() => setIsTransferOwnershipModalOpen(false)}
                gathering={gathering}
                onSuccess={handleTransferOwnershipSuccess}
                token={token}
              />
            )}
          </>
        ) : null}
      </Box>
    </Modal>
  );
};

export default GatheringDetailModal;